// SendStateButton — the Send/Stop/Retry state machine as ONE continuous
// control, pure.
//
// Promoted from sketches/chat-composer/SendStateButton.tsx. Research finding
// (.claude/analysis/chat-composer-research.md §5, §7.1): a premium composer
// swaps the send button's whole IDENTITY — icon, label, tone — as a turn
// moves idle → ready → streaming → error, rather than greying one button
// out. `state` is `SendState` (sendState.ts) — a derived value, never a
// caller-authored prop — so this component only ever renders one of the
// four legal identities.
//
// Pure/props-driven: `state` picks the identity, every click reports up. No
// icon exists yet for "stop" in the shared Icon registry — the glyph is a
// plain filled square (the universal transport-control convention), drawn
// in CSS rather than added to Icon.tsx (this control is the one place that
// needs it; growing the shared registry for a single caller is premature).

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { Icon } from "../../Icon/Icon.js";
import { IconButton } from "../../IconButton/IconButton.js";
import type { SendState } from "./sendState.js";
import "./SendStateButton.css";

export type SendStateButtonProps = {
    readonly state: SendState;
    readonly onSend: () => void;
    readonly onStop: () => void;
    readonly onRetry: () => void;
};

export const SendStateButton = ({
    state,
    onSend,
    onStop,
    onRetry,
}: SendStateButtonProps) => {
    const { t } = useLingui();
    switch (state.type) {
        case "idle":
            return (
                <IconButton label={t`Send`} variant="primary" disabled>
                    <Icon name="arrow-up" />
                </IconButton>
            );
        case "ready":
            return (
                <IconButton label={t`Send`} variant="primary" onClick={onSend}>
                    <Icon name="arrow-up" />
                </IconButton>
            );
        case "streaming":
            return (
                <IconButton
                    label={t`Stop generating`}
                    variant="secondary"
                    onClick={onStop}
                >
                    <span
                        aria-hidden="true"
                        className="bilda-composer-send-state__stop-glyph"
                    />
                </IconButton>
            );
        case "error":
            return (
                <IconButton
                    label={t`Retry`}
                    variant="secondary"
                    onClick={onRetry}
                >
                    <Icon name="redo" />
                </IconButton>
            );
        default:
            return exhaustSwitchCase(state);
    }
};
